import React, { useRef, useState, useEffect, useMemo } from "react";
import { Button } from "./ui/button";
import { ArrowRight, Waves, Star, Eye } from "lucide-react";

const HeroSection = ({ introFinished = true }) => {
  // Flag pour éviter les conflits de scroll
  const isScrollingRef = useRef(false);

  // Animations Trust Indicators — ne démarrent qu'après introFinished
  const [trustVisible, setTrustVisible] = useState(false);
  const [starsCount, setStarsCount] = useState(0);
  const [{ satisfaction, discount }, setTrustMetrics] = useState({
    satisfaction: 0,
    discount: 0,
  });
  const trustRef = useRef(null);

  const particles = useMemo(
    () =>
      [...Array(6)].map(() => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${5 + Math.random() * 4}s`,
      })),
    [],
  );

  // Déclenche les animations trust juste après l'apparition du texte
  useEffect(() => {
    if (!introFinished) return;
    const timer = setTimeout(() => setTrustVisible(true), 800);
    return () => clearTimeout(timer);
  }, [introFinished]);

  // Étoiles : apparition une à une (250ms entre chaque)
  useEffect(() => {
    if (!trustVisible) return;
    const timers = [];
    for (let i = 1; i <= 5; i++) {
      timers.push(setTimeout(() => setStarsCount(i), i * 250));
    }
    return () => timers.forEach(clearTimeout);
  }, [trustVisible]);

  // Compteurs : satisfaction 0→100, discount 0→30 (durée 2s)
  useEffect(() => {
    if (!trustVisible) return;

    const duration = 2000;
    const start = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const intervalId = window.setInterval(() => {
      const elapsed = Math.min((performance.now() - start) / duration, 1);
      const eased = easeOut(elapsed);
      const nextSatisfaction = Math.round(eased * 100);
      const nextDiscount = Math.round(eased * 30);

      setTrustMetrics((prev) => {
        if (
          prev.satisfaction === nextSatisfaction &&
          prev.discount === nextDiscount
        ) {
          return prev;
        }

        return {
          satisfaction: nextSatisfaction,
          discount: nextDiscount,
        };
      });

      if (elapsed >= 1) {
        window.clearInterval(intervalId);
      }
    }, 45);

    return () => window.clearInterval(intervalId);
  }, [trustVisible]);

  const scrollToSection = (sectionId) => {
    // Éviter les conflits si un scroll est déjà en cours
    if (isScrollingRef.current) return;

    isScrollingRef.current = true;

    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }

    // Réinitialiser le flag après l'animation (durée smooth scroll ~800ms)
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 1000);
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Wave Background - Atténué pour plus de sobriété */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
        <div className={`absolute inset-0 opacity-15 ${introFinished ? '' : 'hidden'}`}>
          <svg
            className="absolute bottom-0 w-full h-64"
            viewBox="0 0 1200 200"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,100 Q300,50 600,100 T1200,100 L1200,200 L0,200 Z"
              fill="url(#gradient1)"
              className="animate-wave"
            />
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.4" />
                <stop offset="30%" stopColor="#F87171" stopOpacity="0.25" />
                <stop offset="70%" stopColor="#06B6D4" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>
          
          <svg
            className="absolute bottom-0 w-full h-48"
            viewBox="0 0 1200 200"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 Q400,60 800,120 T1200,120 L1200,200 L0,200 Z"
              fill="url(#gradient2)"
              className="animate-wave-slow"
            />
            <defs>
              <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F87171" stopOpacity="0.25" />
                <stop offset="50%" stopColor="#1e3a8a" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Floating Particles - Seulement après intro */}
      {introFinished && (
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <div
              key={i}
              className="absolute w-1.5 h-1.5 bg-cyan-400/15 rounded-full animate-float"
              style={particle}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 text-center py-20">
        <div className="max-w-5xl mx-auto" style={{
          opacity: introFinished ? 1 : 0,
          transform: introFinished ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
        }}>
          {/* Animated Text - Huge Style avec surbrillance */}
          <div className="overflow-hidden mb-8 relative">
            {/* Surbrillance verticale animée — seulement après intro */}
            {introFinished && (
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute w-32 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-spotlight" style={{ left: '-200px' }}></div>
              </div>
            )}
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-tight tracking-wide relative z-10">
              {/* SEO: Texte accessible aux moteurs de recherche */}
              <span className="sr-only">VyntraWeb - Création de sites web professionnels pour commerces locaux à Thionville, Metz et Luxembourg</span>
              <div className={introFinished ? "animate-text-reveal-up text-white mb-2" : "text-white mb-2 opacity-0"} style={ introFinished ? { 
                animationDelay: '0s',
                textShadow: '0 2px 10px rgba(255, 255, 255, 0.1), 0 0 30px rgba(6, 182, 212, 0.3)'
              } : undefined} aria-hidden="true">
                V&nbsp;Y&nbsp;N&nbsp;T&nbsp;R&nbsp;A
              </div>
              <div className={introFinished ? "animate-text-reveal-up bg-gradient-to-r from-[#00E6FF] via-[#FF7E91] to-[#FF96A6] bg-clip-text text-transparent mb-3" : "bg-gradient-to-r from-[#00E6FF] via-[#FF7E91] to-[#FF96A6] bg-clip-text text-transparent mb-3 opacity-0"} style={ introFinished ? { 
                animationDelay: '0.2s',
                textShadow: '0 2px 10px rgba(255, 126, 145, 0.4)'
              } : undefined} aria-hidden="true">
                W&nbsp;E&nbsp;B
              </div>
            </h1>
          </div>

          {/* Badge */}
          <div className={`inline-flex items-center space-x-2 bg-[#00E6FF]/15 text-[#00E6FF] px-3 py-1.5 rounded-full mb-6 backdrop-blur-sm border border-[#00E6FF]/20 ${introFinished ? 'animate-fade-in-up' : 'opacity-0'}`} style={introFinished ? { animationDelay: '0.3s' } : undefined} role="text" aria-label="Agence Web spécialisée commerces locaux Thionville Metz">
            <Waves size={14} aria-hidden="true" />
            <span className="text-xs font-medium">Agence Web pour Commerces Locaux</span>
          </div>
          
          {/* Subtitle with staggered animation - H2 pour SEO */}
          <div className="space-y-3 mb-8">
            <h2 className={introFinished ? "animate-text-reveal-up text-2xl md:text-3xl lg:text-4xl text-white font-medium tracking-wide mb-2" : "text-2xl md:text-3xl lg:text-4xl text-white font-medium tracking-wide mb-2 opacity-0"} style={introFinished ? { animationDelay: '0.35s' } : undefined}>
              Transformez votre commerce local en <span className="text-[#F87171] font-bold">succès digital</span>
            </h2>
          </div>
          
          {/* Slogan principal */}
          <div className={introFinished ? "animate-fade-in-up text-slate-200 text-xl md:text-2xl font-medium leading-relaxed mb-12" : "text-slate-200 text-xl md:text-2xl font-medium leading-relaxed mb-12 opacity-0"} style={introFinished ? { animationDelay: '0.5s' } : undefined}>
            ⚡ <span className="text-white">Un site web qui</span> 
            <span className="text-[#00E6FF] font-bold"> attire</span>, 
            <span className="text-[#FF7E91] font-bold"> engage</span> et 
            <span className="text-[#AE52FF] font-bold"> convertit</span>
          </div>

          {/* CTA Premium Button - Lueur atténuée pour plus de sobriété */}
          <div className="flex justify-center mb-16">
            <button
              onClick={() => scrollToSection('contact')}
              className={`group relative inline-flex items-center justify-center px-12 py-6 text-lg font-bold text-white transition-all duration-500 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F87171] ${introFinished ? 'animate-fade-in-up' : 'opacity-0'}`}
              aria-label="Démarrer votre projet - Aller au formulaire de contact"
              style={ introFinished ? { 
                animationDelay: '0.65s',
                borderRadius: '16px'
              } : { borderRadius: '16px' }}
            >
              {/* Background gradients - Gradient rose saumon doux */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF7E91] to-[#FF96A6] transition-all duration-300 group-hover:shadow-xl" style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(255, 126, 145, 0.25)'
              }}></div>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ borderRadius: '16px' }}></div>
              
              {/* Content */}
              <span className="relative flex items-center space-x-3">
                <span className="tracking-wide">DÉMARREZ VOTRE PROJET DÈS MAINTENANT</span>
                <ArrowRight className="group-hover:translate-x-2 transition-transform duration-300" size={20} />
              </span>
              
              {/* Lueur subtile (réduite) */}
              <div className="absolute inset-0 bg-[#FF7E91] blur-xl opacity-15 group-hover:opacity-40 transition-opacity duration-300 -z-10 scale-125" style={{ borderRadius: '20px' }}></div>
            </button>
          </div>

          {/* Secondary CTA - Plus discret pour hiérarchiser */}
          <div className="flex justify-center">
            <Button 
              onClick={() => scrollToSection('portfolio')}
              variant="outline"
              size="lg"
              className={`border border-white/20 text-slate-300 bg-transparent hover:bg-white/5 hover:text-white hover:border-white/40 px-8 py-4 rounded-xl font-medium tracking-wide transition-all duration-300 backdrop-blur-sm ${introFinished ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={introFinished ? { animationDelay: '0.8s' } : undefined}
            >
              <Eye className="mr-2" size={18} />
              VOIR NOS RÉALISATIONS
            </Button>
          </div>

          {/* Trust Indicators — apparaissent après le texte hero avec cascade */}
          <div ref={trustRef} className={`grid min-h-[120px] grid-cols-1 md:grid-cols-3 gap-8 pt-12 border-t border-[#00E6FF]/20 ${introFinished ? '' : 'opacity-0'}`}>
            {/* Étoiles — colonne 1 */}
            <div className="text-center" style={{
              opacity: trustVisible ? 1 : 0,
              transform: trustVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
            }}>
              <div className="flex items-center justify-center space-x-1 mb-2 min-h-6" data-testid="trust-stars">
                {[...Array(5)].map((_, i) => {
                  const active = i < starsCount;
                  return (
                    <Star
                      key={i}
                      size={20}
                      className="transition-all duration-500"
                      style={{
                        color: active ? '#FFD700' : '#334155',
                        fill: active ? 'currentColor' : 'transparent',
                        transform: active ? 'scale(1.15)' : 'scale(0.6)',
                        opacity: active ? 1 : 0.35,
                        filter: active ? 'drop-shadow(0 0 8px rgba(255,215,0,0.6))' : 'none',
                      }}
                    />
                  );
                })}
              </div>
              <div className="text-slate-300 text-sm">
                Qualité professionnelle garantie
              </div>
            </div>

            {/* Satisfaction — colonne 2, décalée de 150ms */}
            <div className="text-center" style={{
              opacity: trustVisible ? 1 : 0,
              transform: trustVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-out 0.15s, transform 0.6s ease-out 0.15s',
            }}>
              <div
                className="text-3xl font-bold text-[#00E6FF] mb-2 tabular-nums"
                data-testid="trust-satisfaction"
                style={{ textShadow: '0 0 18px rgba(0,230,255,0.35)' }}
              >
                <span className="inline-block min-w-[4ch]">{satisfaction}%</span>
              </div>
              <div className="text-slate-300 text-sm">
                Satisfaction client
              </div>
            </div>

            {/* Discount — colonne 3, décalée de 300ms */}
            <div className="text-center" style={{
              opacity: trustVisible ? 1 : 0,
              transform: trustVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease-out 0.3s, transform 0.6s ease-out 0.3s',
            }}>
              <div
                className="text-3xl font-bold text-[#FF7E91] mb-2 tabular-nums"
                data-testid="trust-discount"
                style={{ textShadow: '0 0 18px rgba(255,126,145,0.35)' }}
              >
                <span className="inline-block min-w-[4ch]">-{discount}%</span>
              </div>
              <div className="text-slate-300 text-sm">
                Offre de lancement
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Neo Ocean Wave Divider - Atténué */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg
          className="relative block w-full h-24"
          viewBox="0 0 1200 120"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,80 Q150,40 300,60 Q450,80 600,50 Q750,20 900,60 Q1050,100 1200,40 L1200,120 L0,120 Z"
            fill="url(#neoOceanGradient)"
          />
          <path
            d="M0,90 Q200,50 400,70 Q600,90 800,60 Q1000,30 1200,70 L1200,120 L0,120 Z"
            fill="rgba(248, 113, 113, 0.15)"
            className="animate-wave"
          />
          <defs>
            <linearGradient id="neoOceanGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.5" />
              <stop offset="25%" stopColor="#F87171" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.4" />
              <stop offset="75%" stopColor="#F87171" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#1e3a8a" stopOpacity="0.5" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;